import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Alert,
  Chip,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useAccountingNotes, useMultipleEntityNotes } from '../../hooks/useAccountingNotes';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { AccountingNote } from '../../types/accounting-notes';
import { NoteAddModal } from './NoteAddModal';
import { NoteEditModal } from './NoteEditModal';

interface NotesSectionProps {
  entityType: 'invoice' | 'bill';
  entityId: string;
  showTypeColumn?: boolean; // For reconciliation detail
  allowAdd?: boolean; // Default true
  onNoteAdded?: () => void;
  // For reconciliation detail with multiple entities
  invoiceIds?: string[];
  billIds?: string[];
}

/**
 * Format a date string for display
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * NotesSection - Reusable section for displaying notes with Add/Edit functionality
 */
export const NotesSection: React.FC<NotesSectionProps> = ({
  entityType,
  entityId,
  showTypeColumn = false,
  allowAdd = true,
  onNoteAdded,
  invoiceIds,
  billIds,
}) => {
  const { hasPermission } = useUserPermissions();
  const canEdit = hasPermission('accounting.admin');
  const canView = canEdit || hasPermission('accounting.view');

  // Determine if we're using multiple entity mode (reconciliation detail)
  const isMultipleEntityMode = showTypeColumn && (invoiceIds?.length || billIds?.length);

  // Use appropriate query based on mode
  const singleEntityQuery = useAccountingNotes(entityType, entityId, !isMultipleEntityMode);
  const multipleEntityQuery = useMultipleEntityNotes(
    invoiceIds || [],
    billIds || [],
    !!isMultipleEntityMode
  );

  const { data: notes, isLoading, error, refetch } = isMultipleEntityMode
    ? multipleEntityQuery
    : singleEntityQuery;

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<AccountingNote | null>(null);

  // Dropdown for selecting entity type in reconciliation mode
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const [addEntityType, setAddEntityType] = useState<'invoice' | 'bill'>(entityType);
  const [addEntityId, setAddEntityId] = useState<string>(entityId);

  const handleOpenAddModal = (type?: 'invoice' | 'bill', id?: string) => {
    setAddMenuAnchor(null);
    if (type && id) {
      setAddEntityType(type);
      setAddEntityId(id);
    } else {
      setAddEntityType(entityType);
      setAddEntityId(entityId);
    }
    setAddModalOpen(true);
  };

  const handleEditClick = (note: AccountingNote) => {
    setSelectedNote(note);
    setEditModalOpen(true);
  };

  const handleNoteAdded = () => {
    refetch();
    onNoteAdded?.();
  };

  const handleAddButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMultipleEntityMode) {
      setAddMenuAnchor(event.currentTarget);
    } else {
      handleOpenAddModal();
    }
  };

  if (!canView) {
    return null;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load notes: {error.message}
      </Alert>
    );
  }

  // Render table view for reconciliation detail (with Type column)
  if (showTypeColumn) {
    return (
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Notes</Typography>
          {canEdit && allowAdd && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={handleAddButtonClick}
              >
                Add Note
              </Button>
              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={() => setAddMenuAnchor(null)}
              >
                {invoiceIds?.map((id) => (
                  <MenuItem key={`invoice-${id}`} onClick={() => handleOpenAddModal('invoice', id)}>
                    Add to Invoice {id}
                  </MenuItem>
                ))}
                {billIds?.map((id) => (
                  <MenuItem key={`bill-${id}`} onClick={() => handleOpenAddModal('bill', id)}>
                    Add to Bill {id}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>

        {isLoading ? (
          <Skeleton variant="rectangular" height={120} />
        ) : !notes?.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No notes yet.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Date</TableCell>
                  {canEdit && <TableCell align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {notes.map((note) => (
                  <TableRow key={note.id}>
                    <TableCell>
                      <Chip
                        label={note.entityType === 'invoice' ? 'Invoice' : 'Bill'}
                        size="small"
                        color={note.entityType === 'invoice' ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{note.content}</Typography>
                      {note.updatedAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Edited by {note.updatedByName} on {formatDate(note.updatedAt)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{note.createdByName}</TableCell>
                    <TableCell>{formatDate(note.createdAt)}</TableCell>
                    {canEdit && (
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleEditClick(note)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add Modal */}
        <NoteAddModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          entityType={addEntityType}
          entityId={addEntityId}
          onSuccess={handleNoteAdded}
        />

        {/* Edit Modal */}
        {selectedNote && (
          <NoteEditModal
            open={editModalOpen}
            onClose={() => {
              setEditModalOpen(false);
              setSelectedNote(null);
            }}
            note={selectedNote}
            onSuccess={handleNoteAdded}
          />
        )}
      </Box>
    );
  }

  // Render card view for detail pages
  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Notes</Typography>
        {canEdit && allowAdd && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenAddModal()}
          >
            Add Note
          </Button>
        )}
      </Box>

      {isLoading ? (
        <>
          <Skeleton variant="rectangular" height={80} sx={{ mb: 2, borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
        </>
      ) : !notes?.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No notes yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {notes.map((note) => (
            <Card key={note.id} variant="outlined">
              <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {note.content}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Created by {note.createdByName} on {formatDate(note.createdAt)}
                    </Typography>
                    {note.updatedAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Edited by {note.updatedByName} on {formatDate(note.updatedAt)}
                      </Typography>
                    )}
                  </Box>
                  {canEdit && (
                    <IconButton size="small" onClick={() => handleEditClick(note)} sx={{ ml: 1 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Add Modal */}
      <NoteAddModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        entityType={entityType}
        entityId={entityId}
        onSuccess={handleNoteAdded}
      />

      {/* Edit Modal */}
      {selectedNote && (
        <NoteEditModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedNote(null);
          }}
          note={selectedNote}
          onSuccess={handleNoteAdded}
        />
      )}
    </Box>
  );
};

export default NotesSection;
